import { isNumber, isString } from 'fitify-types/src/type-guards/common'
import { PlanInterval } from 'fitify-types/src/types/subscription'
import { DateTime } from 'luxon'
import { useTranslation } from 'next-i18next/pages'
import { useMemo } from 'react'

import {
  type SubscriptionPageConfig,
  type SubscriptionPlanConfig,
} from '../../types/subscription'

import { useGrowthBookParameters } from './growthbookParameters'

type UseSubscriptionConfigProps = {
  defaults: {
    pageConfig: Pick<SubscriptionPageConfig, 'defaultPeriod' | 'planTitle'>
  }
  promoCode?: string
}

export type UseSubscriptionConfig = {
  pageConfig: SubscriptionPageConfig
  subscriptionPlanConfigs: SubscriptionPlanConfig[]
}

export const useSubscriptionConfig = ({
  defaults,
  promoCode,
}: UseSubscriptionConfigProps): UseSubscriptionConfig => {
  const { t } = useTranslation()
  const { getBooleanParameter, getIntegerParameter, getStringParameter } =
    useGrowthBookParameters(promoCode)

  const expirationMinutes = getIntegerParameter({
    promoKey: `promo_${promoCode}_expiration`,
  })
  const hasCountdown = Boolean(
    getBooleanParameter({
      promoKey: `promo_${promoCode}_countdown`,
    })
  )

  const countdownExpirationMillis = useMemo(() => {
    if (hasCountdown && isNumber(expirationMinutes)) {
      return DateTime.now().plus({ minutes: expirationMinutes }).toMillis()
    }
    return undefined
  }, [expirationMinutes, hasCountdown])

  const pageConfig = useMemo<SubscriptionPageConfig>(() => {
    const getPlanTitle = () => {
      const planTitle = getStringParameter({
        promoKey: `promo_${promoCode}_title`,
      })
      if (planTitle) {
        return t(planTitle, {
          0: getIntegerParameter({
            promoKey: `promo_${promoCode}_discount_percentage`,
          }),
        })
      }
      return undefined
    }

    return {
      addOnEnabled: getBooleanParameter({
        promoKey: `promo_${promoCode}_add_on_enabled`,
      }),
      countdownExpirationMillis,
      checkoutStyle: getStringParameter({
        promoKey: `promo_${promoCode}_checkout_style`,
      }),
      defaultPeriod:
        getStringParameter({
          defaultKey: 'payment_default_period',
          promoKey: `promo_${promoCode}_default_period`,
          shouldUseDefaultParam: true,
        }) || defaults.pageConfig.defaultPeriod,
      discountType: getStringParameter({
        promoKey: `promo_${promoCode}_discount_type`,
      }),
      downSellPromo: getStringParameter({
        promoKey: `promo_${promoCode}_downsell_promo`,
      }),
      flexDiscountId: getStringParameter({
        promoKey: `promo_${promoCode}_flex_discount_id`,
      }),
      gateway: getStringParameter({
        defaultKey: 'payment_gateway',
        promoKey: `promo_${promoCode}_gateway`,
        shouldUseDefaultParam: true,
      }),
      paddleDiscountId: getStringParameter({
        promoKey: `promo_${promoCode}_paddle_discount_id`,
      }),
      planTitle: getPlanTitle() || defaults.pageConfig.planTitle,
      planNamingStyle: getStringParameter({
        promoKey: `promo_${promoCode}_plan_naming_style`,
      }),
      promoDiscountPercentage: getIntegerParameter({
        promoKey: `promo_${promoCode}_discount_percentage`,
      }),
      promoImageUrl: getStringParameter({
        promoKey: `promo_${promoCode}_header_image`,
      }),
    }
  }, [
    countdownExpirationMillis,
    defaults.pageConfig.defaultPeriod,
    defaults.pageConfig.planTitle,
    getBooleanParameter,
    getIntegerParameter,
    getStringParameter,
    promoCode,
    t,
  ])

  const subscriptionPlanConfigs = useMemo<SubscriptionPlanConfig[]>(() => {
    return Object.values(PlanInterval).reduce<SubscriptionPlanConfig[]>(
      (plans, interval) => {
        const isEnabled = getBooleanParameter({
          promoKey: `promo_${promoCode}_${interval}_enabled`,
          defaultKey: `payment_${interval}_enabled`,
        })
        const paddlePriceId = getStringParameter({
          promoKey: `promo_${promoCode}_${interval}_paddle_price_id`,
          defaultKey: `payment_${interval}_paddle_price_id`,
        })
        const flexPriceId = getStringParameter({
          promoKey: `promo_${promoCode}_${interval}_flex_price_id`,
          defaultKey: `payment_${interval}_flex_price_id`,
        })
        const stripePriceId = getStringParameter({
          promoKey: `promo_${promoCode}_${interval}_stripe_price_id`,
          defaultKey: `payment_${interval}_stripe_price_id`,
        })
        const stripeDiscountId = getStringParameter({
          promoKey: `promo_${promoCode}_${interval}_stripe_discount_id`,
        })
        const sku = getStringParameter({
          promoKey: `promo_${promoCode}_${interval}_sku`,
          defaultKey: `payment_${interval}_sku`,
        })
        if (
          typeof isEnabled === 'boolean' &&
          (isString(sku) ||
            isString(paddlePriceId) ||
            isString(flexPriceId) ||
            isString(stripePriceId))
        ) {
          plans.push({
            badge: getStringParameter({
              promoKey: `promo_${promoCode}_${interval}_badge`,
              defaultKey: `payment_${interval}_badge`,
            }),
            badgePlaceholder: getStringParameter({
              promoKey: `promo_${promoCode}_${interval}_badge_placeholder`,
              defaultKey: `payment_${interval}_badge_placeholder`,
            }),
            badgeColor: getStringParameter({
              promoKey: `promo_${promoCode}_badge_color`,
            }),
            flexPriceId,
            isEnabled,
            interval,
            paddlePriceId,
            stripePriceId,
            stripeDiscountId,
            sku,
          })
        }

        return plans
      },
      []
    )
  }, [getBooleanParameter, getStringParameter, promoCode])

  return {
    pageConfig,
    subscriptionPlanConfigs,
  }
}
